import { cs } from '../../../lib/currency';
/**
 * Enrollment Approval Dialog (iter270 — extracted from Enrollments.js).
 *
 * Approves a pending enrollment with program/class selection, payment
 * frequency, monthly rate, optional discount (sibling auto-detected
 * or manual catalog pick), and start date. The parent owns all state
 * + handlers; this component just renders.
 *
 * Props:
 *   - open                     (bool)
 *   - onOpenChange             ((bool) => void)
 *   - selectedEnrollment       (object | null)
 *   - approvalData             (object)
 *   - setApprovalData          ((updater | object) => void)
 *   - programs                 (Array<Program>)
 *   - discounts                (Array<Discount>)
 *   - paymentFrequencies       (Array<{value, label, multiplier}>) — PAYMENT_FREQUENCIES const
 *   - selectedProgram          (Program | undefined)
 *   - siblingCheck             (object | null)
 *   - checkingSibling          (bool)
 *   - getFilteredClasses       (() => Array<Class>)
 *   - onProgramChange          ((programId) => void)
 *   - onClassChange            ((classId) => void)
 *   - onFrequencyChange        ((freq) => void)
 *   - onDiscountChange         ((discountId) => void)
 *   - recalculateFinalFee      ((baseFee, discountId?) => number)
 *   - onApprove                (() => Promise<void>)
 */
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  BookOpen, Calendar, DollarSign, Loader2, Check,
} from 'lucide-react';

export default function ApprovalDialog({
  open,
  onOpenChange,
  selectedEnrollment,
  approvalData,
  setApprovalData,
  programs,
  discounts,
  paymentFrequencies,
  selectedProgram,
  siblingCheck,
  checkingSibling,
  getFilteredClasses,
  onProgramChange,
  onClassChange,
  onFrequencyChange,
  onDiscountChange,
  recalculateFinalFee,
  onApprove,
  prorationPreview,
  prorationPreviewLoading,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Approve Enrollment</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          {/* Player Info */}
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="font-medium text-slate-900">{selectedEnrollment?.player_name}</p>
            <p className="text-sm text-slate-500">
              Parent: {selectedEnrollment?.parent_name}
            </p>
            {selectedEnrollment?.program_name && (
              <p className="text-sm text-blue-600 mt-1">
                Requested: {selectedEnrollment?.program_name}
              </p>
            )}
          </div>

          {/* Program Selection */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-blue-500" />
              Select Program *
            </Label>
            <Select value={approvalData.program_id} onValueChange={onProgramChange}>
              <SelectTrigger data-testid="approval-program-select">
                <SelectValue placeholder="Select a program" />
              </SelectTrigger>
              <SelectContent position="popper" className="max-h-48 overflow-y-auto z-[100]" sideOffset={4}>
                {programs.filter(p => p.is_active !== false).map((program) => (
                  <SelectItem key={program.id} value={program.id}>
                    {program.name} - {cs()}{program.price}/mo
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedProgram && (
              <p className="text-xs text-slate-500">
                Monthly: {cs()}{selectedProgram.price} |
                Quarterly: {cs()}{selectedProgram.quarterly_price || selectedProgram.price} |
                Bi-Annual: {cs()}{selectedProgram.biannual_price || selectedProgram.price}
              </p>
            )}
          </div>

          {/* Class Selection */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-green-500" />
              Assign to Class{selectedProgram?.sessions_per_week > 1 ? 'es' : ''} *
              {selectedProgram?.sessions_per_week > 1 && (
                <span className="text-sm text-slate-500">
                  (Select {selectedProgram.sessions_per_week} classes)
                </span>
              )}
            </Label>

            {selectedProgram?.sessions_per_week > 1 ? (
              <div className="border rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto">
                {getFilteredClasses().length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-2">
                    No classes available for this program
                  </p>
                ) : (
                  getFilteredClasses().map((classItem) => (
                    <div
                      key={classItem.id}
                      className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                        approvalData.class_ids.includes(classItem.id)
                          ? 'bg-green-50 border border-green-200'
                          : 'hover:bg-slate-50 border border-transparent'
                      }`}
                      onClick={() => onClassChange(classItem.id)}
                    >
                      <Checkbox
                        checked={approvalData.class_ids.includes(classItem.id)}
                        disabled={!approvalData.class_ids.includes(classItem.id) && approvalData.class_ids.length >= selectedProgram.sessions_per_week}
                      />
                      <div className="flex-1">
                        <p className="font-medium">{classItem.name}</p>
                        <p className="text-sm text-slate-500">
                          {classItem.schedule_day} {classItem.start_time} - {classItem.end_time}
                        </p>
                      </div>
                    </div>
                  ))
                )}
                {approvalData.class_ids.length > 0 && (
                  <p className="text-xs text-green-600 pt-2 border-t">
                    {approvalData.class_ids.length} of {selectedProgram.sessions_per_week} class(es) selected
                  </p>
                )}
              </div>
            ) : (
              <Select
                value={approvalData.class_id}
                onValueChange={onClassChange}
                disabled={!approvalData.program_id}
              >
                <SelectTrigger data-testid="approval-class-select">
                  <SelectValue placeholder={approvalData.program_id ? 'Select a class' : 'Select program first'} />
                </SelectTrigger>
                <SelectContent position="popper" className="max-h-48 overflow-y-auto z-[100]" sideOffset={4}>
                  {getFilteredClasses().length === 0 ? (
                    <div className="px-2 py-3 text-center text-sm text-slate-500">
                      No classes available for this program
                    </div>
                  ) : (
                    getFilteredClasses().map((classItem) => (
                      <SelectItem key={classItem.id} value={classItem.id}>
                        {classItem.name} - {classItem.schedule_day} {classItem.schedule_time}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Payment Plan */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-amber-500" />
              Payment Plan
            </Label>
            <Select value={approvalData.payment_frequency} onValueChange={onFrequencyChange}>
              <SelectTrigger data-testid="approval-frequency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent position="popper" className="z-[100]" sideOffset={4}>
                {paymentFrequencies.map((freq) => (
                  <SelectItem key={freq.value} value={freq.value}>
                    {freq.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Monthly Rate */}
          <div className="space-y-2">
            <Label>Monthly Rate ($)</Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={approvalData.fee_amount}
              onChange={(e) => {
                const newFee = parseFloat(e.target.value) || 0;
                const finalFee = recalculateFinalFee(newFee);
                setApprovalData({ ...approvalData, fee_amount: newFee, final_fee: finalFee });
              }}
              data-testid="approval-fee-input"
            />
          </div>

          {/* Pro-rate first month — explicit admin toggle. Defaulted ON
              so mid-month approvals don't silently bill the full monthly
              fee. Matches the QuickEnrollDialog pattern. */}
          {approvalData.program_id && (
            <label
              className="flex items-start gap-2 p-3 rounded-lg border bg-amber-50 border-amber-200 cursor-pointer"
              data-testid="approval-prorate-block"
            >
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 text-emerald-600 rounded"
                checked={approvalData.apply_proration !== false}
                onChange={(e) =>
                  setApprovalData({
                    ...approvalData,
                    apply_proration: e.target.checked,
                  })
                }
                data-testid="approval-prorate-checkbox"
              />
              <span className="text-sm text-amber-900">
                <span className="font-medium">Pro-rate first month</span>
                <span className="block text-xs text-amber-700 mt-0.5">
                  When the start date is mid-month, charge only the remaining sessions
                  for this month. Recurring billing then resumes on the 1st.
                </span>
                {/* iter271: live preview of today's prorated charge + next
                    billing date, sourced from
                    GET /api/enrollments/proration-preview. Driven by the
                    parent (Enrollments.js) so the network call is shared
                    state, not coupled to the dialog. */}
                {approvalData.apply_proration !== false && (() => {
                  if (prorationPreviewLoading) {
                    return (
                      <span className="block mt-1.5 text-xs text-amber-700 italic" data-testid="approval-prorate-preview-loading">
                        Calculating preview…
                      </span>
                    );
                  }
                  if (!prorationPreview) return null;
                  // Next billing date = first day of the month after start_date.
                  const sd = approvalData.start_date ? new Date(`${approvalData.start_date}T00:00:00`) : null;
                  let nextBillingLabel = '';
                  if (sd && !Number.isNaN(sd.getTime())) {
                    const next = new Date(sd.getFullYear(), sd.getMonth() + 1, 1);
                    nextBillingLabel = next.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                  }
                  if (prorationPreview.is_prorated) {
                    return (
                      <span
                        className="block mt-1.5 text-xs font-medium text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1"
                        data-testid="approval-prorate-preview"
                      >
                        {cs()}{Number(prorationPreview.prorated_amount).toFixed(2)} today
                        {' '}({prorationPreview.remaining_sessions}/{prorationPreview.total_sessions} sessions),
                        {' '}then {cs()}{Number(prorationPreview.monthly_fee).toFixed(2)}/mo
                        {nextBillingLabel ? ` from ${nextBillingLabel}` : ''}.
                      </span>
                    );
                  }
                  return (
                    <span
                      className="block mt-1.5 text-xs text-slate-600 bg-white border border-amber-200 rounded px-2 py-1"
                      data-testid="approval-prorate-preview-noop"
                    >
                      Starting at the beginning of the billing period — no proration needed.
                    </span>
                  );
                })()}
              </span>
            </label>
          )}

          {/* Discount Section */}
          <div className="space-y-3 p-4 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg border border-purple-200">
            <Label className="flex items-center gap-2 text-purple-800">
              <DollarSign className="h-4 w-4" />
              Apply Discount
            </Label>

            {checkingSibling && (
              <div className="p-2 bg-white rounded border flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-purple-500" />
                <span className="text-sm text-purple-600">Checking sibling discount eligibility...</span>
              </div>
            )}

            {siblingCheck && siblingCheck.eligible && siblingCheck.apply_to_current_enrollment && (
              <div className="p-3 bg-green-100 rounded-lg border border-green-300">
                <div className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-green-800">Sibling Discount Eligible!</p>
                    <p className="text-sm text-green-700">{siblingCheck.message}</p>
                    {siblingCheck.discount && (
                      <div className="mt-2 text-sm">
                        <span className="font-medium">Auto-applied:</span> {siblingCheck.discount.name}
                        ({siblingCheck.discount.value}{siblingCheck.discount.type === 'percentage' ? '%' : '$'} off)
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {siblingCheck && !siblingCheck.eligible && siblingCheck.reason && (
              <div className="p-2 bg-slate-100 rounded text-sm text-slate-600">
                {siblingCheck.reason}
              </div>
            )}

            {siblingCheck && siblingCheck.needs_discount_setup && (
              <div className="p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
                ⚠️ Multiple children enrolled but no sibling discount is configured.
                <a href="/admin/discounts" className="underline ml-1">Set up sibling discount</a>
              </div>
            )}

            {/* Manual Discount Selection */}
            <div className="space-y-2">
              <Label className="text-sm">Or Select Discount:</Label>
              <Select value={approvalData.discount_id || 'none'} onValueChange={onDiscountChange}>
                <SelectTrigger data-testid="discount-select">
                  <SelectValue placeholder="No discount" />
                </SelectTrigger>
                <SelectContent position="popper" className="z-[100]" sideOffset={4}>
                  <SelectItem value="none">No discount</SelectItem>
                  {discounts.map((discount) => (
                    <SelectItem key={discount.id} value={discount.id}>
                      {discount.name} ({discount.value ?? discount.discount_value}{discount.discount_type === 'percentage' ? '%' : '$'} off)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Fee Breakdown */}
            {approvalData.discount_id && (
              <div className="p-3 bg-white rounded-lg border border-purple-200 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Original Fee:</span>
                  <span className="line-through text-slate-400">{cs()}{approvalData.fee_amount.toFixed(2)}/mo</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-purple-600">{approvalData.discount_name}:</span>
                  <span className="text-purple-600">
                    -{approvalData.discount_type === 'percentage'
                      ? `${approvalData.discount_value}%`
                      : `${cs()}${approvalData.discount_value}`}
                  </span>
                </div>
                <div className="flex justify-between font-semibold pt-2 border-t">
                  <span className="text-green-700">Final Monthly Fee:</span>
                  <span className="text-green-700 text-lg">{cs()}{approvalData.final_fee.toFixed(2)}/mo</span>
                </div>
                {approvalData.is_sibling_discount && (
                  <p className="text-xs text-purple-500 mt-1">
                    ✓ Sibling discount applied (lowest fee program)
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Total Amount Summary */}
          <div className="p-3 bg-green-50 rounded-lg border border-green-200">
            <p className="text-sm text-green-800">
              <strong>Total to charge:</strong>{' '}
              <span className="text-lg font-bold">
                {cs()}{((approvalData.discount_id ? approvalData.final_fee : approvalData.fee_amount) *
                  (paymentFrequencies.find(f => f.value === approvalData.payment_frequency)?.multiplier || 1)).toFixed(2)}
              </span>
              {approvalData.payment_frequency !== 'monthly' && (
                <span className="ml-1 text-green-600">
                  ({paymentFrequencies.find(f => f.value === approvalData.payment_frequency)?.multiplier} months)
                </span>
              )}
            </p>
            {approvalData.discount_id && (
              <p className="text-xs text-green-600 mt-1">
                You save {cs()}{((approvalData.fee_amount - approvalData.final_fee) *
                  (paymentFrequencies.find(f => f.value === approvalData.payment_frequency)?.multiplier || 1)).toFixed(2)} with {approvalData.discount_name}
              </p>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes (Optional)</Label>
            <Input
              value={approvalData.notes}
              onChange={(e) => setApprovalData({ ...approvalData, notes: e.target.value })}
              placeholder="Any notes for the parent..."
              data-testid="approval-notes-input"
            />
          </div>

          {/*
            iter316 — Per-enrollment billing cadence override.
            Blank (default) means "use tenant default from
            /admin/billing-cadence". Values match backend canonical
            strings.
          */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              Billing cadence
              <span className="text-[10px] uppercase tracking-wide text-slate-400">
                override
              </span>
            </Label>
            <select
              value={approvalData.billing_cadence || ''}
              onChange={(e) => setApprovalData({
                ...approvalData,
                billing_cadence: e.target.value || '',
              })}
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
              data-testid="approval-billing-cadence-select"
            >
              <option value="">Use tenant default</option>
              <option value="first_of_month">First of the month</option>
              <option value="first_of_enrollment">First of enrollment</option>
            </select>
            <p className="text-[11px] text-slate-500">
              Leave as <em>tenant default</em> unless this family wants
              a different cycle policy than the rest of the roster.
            </p>
          </div>

          {/* Start Date */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-blue-500" />
              Start Date
            </Label>
            <Input
              type="date"
              value={approvalData.start_date}
              onChange={(e) => setApprovalData({ ...approvalData, start_date: e.target.value })}
              data-testid="approval-start-date-input"
            />
            <p className="text-xs text-slate-500">When does this enrollment become active?</p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              className="bg-blue-500 hover:bg-blue-600"
              onClick={onApprove}
              disabled={!approvalData.program_id || !approvalData.class_id}
              data-testid="confirm-approval-btn"
            >
              Approve & Assign
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
